/**
 * HTTP client for the Worker admin signup-allowlist surface.
 *
 * Mirrors `admin/api.ts`:
 *   - cookie-credentialed (the session cookie is the load-bearing auth
 *     token; the worker's `requireSystemAdmin` returns **404** for
 *     non-admins so the route's existence is not leaked).
 *   - resolves to a discriminated `AdminResult<T>` so the view can branch
 *     on `not-found` / `unauthenticated` / `error`.
 */
import { createAdminRequest, type AdminResult } from './admin-fetch.js';

export type { AdminResult } from './admin-fetch.js';

const request = createAdminRequest('/v1/admin/signups');

export interface AllowlistEntry {
  email: string;
  added_by: string | null;
  added_at: string;
  note: string | null;
}

export interface SignupsState {
  enabled: boolean;
  allowlist: AllowlistEntry[];
}

export function getSignups(): Promise<AdminResult<SignupsState>> {
  // No trailing slash: a mounted Hono sub-app's `GET /` matches
  // `/v1/admin/signups` but NOT `/v1/admin/signups/` (the trailing slash
  // misses the route and collapses to the gate's 404). Without this the
  // page renders the not-found overlay even for a real admin.
  return request('');
}

export function setSignupMode(enabled: boolean): Promise<AdminResult<{ ok: true; enabled: boolean }>> {
  return request('/mode', { method: 'PUT', body: JSON.stringify({ enabled }) });
}

export function addAllowlistEmail(
  email: string,
  note?: string,
): Promise<AdminResult<AllowlistEntry>> {
  const body: { email: string; note?: string } = { email };
  if (note !== undefined && note.trim() !== '') body.note = note.trim();
  return request('/allowlist', { method: 'POST', body: JSON.stringify(body) });
}

export function removeAllowlistEmail(email: string): Promise<AdminResult<{ ok: true }>> {
  return request(`/allowlist/${encodeURIComponent(email)}`, { method: 'DELETE' });
}
